import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Target, Trophy, Users, CheckCircle2, Star } from 'lucide-react';
import { ProofSection } from '../components/ProofSection';
import { Navigation } from '../components/Navigation';
// Animation variants
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
export function HomePage() {
  const scrollToForm = () => {
    const formElement = document.getElementById('lead-form');
    if (formElement) {
      formElement.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <div className="min-h-screen bg-white font-sans text-gray-800" data-id="element-269">
      <Navigation data-id="element-270" />

      {/* Hero Section */}
      <section className="relative pt-12 pb-20 px-4 overflow-hidden bg-gradient-to-b from-blue-50 to-white" data-id="element-271">
        <div className="max-w-4xl mx-auto text-center" data-id="element-272">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="space-y-6" data-id="element-273">
            <span className="inline-block px-4 py-1.5 rounded-full bg-red-100 text-red-700 text-sm font-bold tracking-wide uppercase mb-2" data-id="element-274">
              Northwest Black Belt Academy
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-[#1e3a8a] leading-tight tracking-tight" data-id="element-275">
              Build Confidence, Discipline & Focus in Your Child
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed" data-id="element-276">
              Watch them transform in just 2 weeks. Give your child the tools to
              succeed in school and life through martial arts.
            </p>

            <div className="pt-4" data-id="element-277">
              <motion.button whileTap={{
              scale: 0.95
            }} onClick={scrollToForm} className="w-full sm:w-auto px-8 py-4 bg-[#dc2626] text-white text-lg font-bold rounded-lg shadow-lg shadow-red-200 hover:bg-red-700 transition-colors" data-id="element-278">
                Start Your $29 Trial
              </motion.button>
              <p className="mt-3 text-sm text-gray-500" data-id="element-279">
                Limited time offer • No commitment required
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 px-4 bg-white" data-id="element-280">
        <div className="max-w-4xl mx-auto" data-id="element-281">
          <div className="text-center mb-12" data-id="element-282">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4" data-id="element-283">
              Is your child facing these challenges?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-284">
            {[{
            title: 'Struggling with Focus?',
            desc: 'Difficulty paying attention at school or following directions at home.'
          }, {
            title: 'Low Confidence?',
            desc: 'Shying away from challenges or hesitant to speak up in groups.'
          }, {
            title: 'Too Much Screen Time?',
            desc: 'Spending hours on tablets instead of moving and interacting.'
          }].map((item, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-gray-50 p-6 rounded-xl border border-gray-100 shadow-sm" data-id="element-285">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-286">
                  <span className="text-red-600 text-xl font-bold" data-id="element-287">?</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-288">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-289">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-[#1e3a8a] text-white" data-id="element-290">
        <div className="max-w-4xl mx-auto" data-id="element-291">
          <div className="text-center mb-16" data-id="element-292">
            <h2 className="text-3xl sm:text-4xl font-black mb-4" data-id="element-293">
              More Than Just Kicks & Punches
            </h2>
            <p className="text-blue-100 text-lg max-w-2xl mx-auto" data-id="element-294">
              We don't just teach self-defense. We teach character development
              that lasts a lifetime.
            </p>
          </div>

          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-295">
            {[{
            icon: Trophy,
            title: 'Unshakable Confidence',
            desc: 'Kids learn to believe in themselves, speak up, and stand tall in any situation.'
          }, {
            icon: Target,
            title: 'Laser Focus',
            desc: 'Improved concentration that translates directly to better grades and behavior.'
          }, {
            icon: ShieldCheck,
            title: 'Discipline & Respect',
            desc: 'Learning to listen, follow directions, and respect authority figures.'
          }, {
            icon: Users,
            title: 'Positive Community',
            desc: 'Surrounded by supportive mentors and friends who encourage growth.'
          }].map((benefit, idx) => <motion.div key={idx} variants={fadeIn} className="flex gap-4 items-start" data-id="element-296">
                <div className="bg-blue-800 p-3 rounded-lg shrink-0" data-id="element-297">
                  <benefit.icon className="w-6 h-6 text-blue-200" data-id="element-298" />
                </div>
                <div data-id="element-299">
                  <h3 className="text-xl font-bold mb-2" data-id="element-300">{benefit.title}</h3>
                  <p className="text-blue-100 leading-relaxed" data-id="element-301">
                    {benefit.desc}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 px-4 bg-gray-50" data-id="element-302">
        <div className="max-w-3xl mx-auto" data-id="element-303">
          <div className="text-center mb-12" data-id="element-304">
            <div className="flex justify-center gap-1 mb-4" data-id="element-305">
              {[1, 2, 3, 4, 5].map((_, i) => <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" data-id="element-306" />)}
            </div>
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-307">
              Trusted by 500+ Local Families
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-308">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-309">
              <p className="text-gray-600 italic mb-4" data-id="element-310">
                "The changes in my son's general attitude & performance are
                significant & noticeable. Mrs. Maria is a miracle worker, she
                handles most of if not all the paper work, registration,
                fundraising, billing. You got questions, she has Answers. Bottom
                line: This is a Great School to be educated. Come join the
                family, you won't regret it!"
              </p>
              <div className="font-bold text-gray-900" data-id="element-311">
                — Paul Manudat, Parent
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-312">
              <p className="text-gray-600 italic mb-4" data-id="element-313">
                "Master Joe and the team of instructors & assistants are serious
                professionals who are dedicated to developing competitive, well
                rounded and disciplined athletes of the future. They pay
                meticulous attention to detail on every student's individual
                performance and work closely with them to bring out their best."
              </p>
              <div className="font-bold text-gray-900" data-id="element-314">
                — Ruchi Paul, Parent
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Offer Section */}
      <section className="py-20 px-4 bg-white" data-id="element-315">
        <div className="max-w-4xl mx-auto" data-id="element-316">
          <div className="bg-[#1e3a8a] rounded-2xl overflow-hidden shadow-2xl" data-id="element-317">
            <div className="grid grid-cols-1 md:grid-cols-2" data-id="element-318">
              <div className="p-8 md:p-12 text-white flex flex-col justify-center" data-id="element-319">
                <div className="inline-block px-3 py-1 bg-red-600 text-xs font-bold uppercase tracking-wider rounded mb-4 w-fit" data-id="element-320">
                  Limited Time Offer
                </div>
                <h2 className="text-3xl md:text-4xl font-black mb-4" data-id="element-321">
                  2 Weeks for Just $29
                </h2>
                <ul className="space-y-3 mb-8" data-id="element-322">
                  {['Unlimited classes for 2 weeks', 'Official uniform included (Free)', 'Personal intro session', 'No long-term commitment'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-323">
                      <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" data-id="element-324" />
                      <span className="text-blue-100" data-id="element-325">{item}</span>
                    </li>)}
                </ul>
                <p className="text-sm text-blue-200 italic" data-id="element-326">
                  *Only 5 spots remaining for this month's intake
                </p>
              </div>

              <div className="bg-gray-50 p-8 md:p-12 flex flex-col justify-center" id="lead-form" data-id="element-327">
                <div className="text-center mb-6" data-id="element-328">
                  <h3 className="text-2xl font-bold text-gray-900" data-id="element-329">
                    Claim Your Spot
                  </h3>
                  <p className="text-gray-600 text-sm" data-id="element-330">
                    Fill out the form below to get started.
                  </p>
                </div>

                <form className="space-y-4" onSubmit={e => {
                e.preventDefault();
                console.log('Form submitted');
              }} data-id="element-331">
                  <div data-id="element-332">
                    <label htmlFor="name" className="sr-only" data-id="element-333">
                      Parent's Name
                    </label>
                    <input type="text" id="name" placeholder="Parent's Name" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent outline-none transition-all" required data-id="element-334" />
                  </div>
                  <div data-id="element-335">
                    <label htmlFor="phone" className="sr-only" data-id="element-336">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" placeholder="Phone Number" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent outline-none transition-all" required data-id="element-337" />
                  </div>
                  <div data-id="element-338">
                    <label htmlFor="childAge" className="sr-only" data-id="element-339">
                      Child's Age
                    </label>
                    <input type="number" id="childAge" placeholder="Child's Age" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent outline-none transition-all" required data-id="element-340" />
                  </div>
                  <motion.button whileTap={{
                  scale: 0.98
                }} type="submit" className="w-full py-4 bg-[#dc2626] text-white font-bold rounded-lg shadow-md hover:bg-red-700 transition-colors text-lg" data-id="element-341">
                    Get Started Now
                  </motion.button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof Section */}
      <ProofSection testimonialIndex={0} data-id="element-342" />

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 text-center text-sm" data-id="element-343">
        <div className="max-w-4xl mx-auto" data-id="element-344">
          <div className="font-black text-xl text-white mb-4 tracking-tighter" data-id="element-345">
            NWBBA
          </div>
          <p className="mb-6" data-id="element-346">Northwest Black Belt Academy</p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-8" data-id="element-347">
            <Link to="/" className="text-white font-bold" data-id="element-348">
              Home
            </Link>
            <Link to="/about" className="hover:text-white transition-colors" data-id="element-349">
              About
            </Link>
            <Link to="/classes" className="hover:text-white transition-colors" data-id="element-350">
              Classes
            </Link>
            <Link to="/schedule" className="hover:text-white transition-colors" data-id="element-351">
              Schedule
            </Link>
            <Link to="/resources" className="hover:text-white transition-colors" data-id="element-352">
              Resources
            </Link>
            <Link to="/blog" className="hover:text-white transition-colors" data-id="element-353">
              Blog
            </Link>
            <Link to="/proof" className="hover:text-white transition-colors" data-id="element-354">
              Proof
            </Link>
            <a href="#" className="hover:text-white transition-colors" data-id="element-355">
              Contact
            </a>
          </div>
          <p data-id="element-356">
            &copy; {new Date().getFullYear()} Northwest Black Belt Academy. All
            rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}